import AppComponent from '../base/app-component.ts';
import { apiGet } from '../lib/api-client.ts';
import { templateParseToElement } from '../lib/template-parse.ts';

interface ImpresaLite {
  id: number;
  codImp: string;
  ragioneSociale: string | null;
  partitaIva: string | null;
  codiceFiscale: string | null;
  citta: string | null;
  origine: 'CASSA' | 'MANUALE';
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

/**
 * wc-impresa-picker - Autocomplete impresa con tasto "+ Nuova" inline.
 *
 * Attributi:
 *   placeholder (string, default "Cerca impresa per ragione sociale, P.IVA o CF")
 *   allow-create (bool, default true) - se true mostra "+ Nuova impresa"
 *
 * Eventi emessi:
 *   impresa-selected   detail: ImpresaLite
 *   impresa-cleared    detail: {}
 */
export class ImpresaPicker extends AppComponent {
  static override componentName = 'wc-impresa-picker';
  protected override get templateId() { return 'impresa-picker'; }

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastQuery = '';
  private selected: ImpresaLite | null = null;
  private modals: { dispose(): void }[] = [];
  private cache = new Map<number, ImpresaLite>();

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      placeholder: this.attributeString('placeholder', 'Cerca impresa per ragione sociale, P.IVA o CF'),
      allowCreate: this.attributeBoolean('allow-create', true),
    };
  }

  protected override afterRender(): void {
    this.delegate.on<InputEvent>('input', '[data-picker-input]', (e) => {
      const value = (e.target as HTMLInputElement).value.trim();
      this.scheduleSearch(value);
    });

    this.delegate.on<MouseEvent>('click', '[data-action="picker-clear"]', () => {
      this.clearSelection();
    });

    this.delegate.on<MouseEvent>('click', '[data-action="picker-select"]', (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-impresa-id]');
      if (!row) return;
      const id = Number(row.dataset.impresaId);
      const impresa = this.cache.get(id);
      if (impresa) this.selectImpresa(impresa);
    });

    this.delegate.on<MouseEvent>('click', '[data-action="picker-nuova"]', () => {
      this.openNuovaImpresaModal();
    });

    this.listen(document, 'impresa-salvata', this.handleImpresaSalvata);
  }

  protected override onDisconnected(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    document.removeEventListener('impresa-salvata', this.handleImpresaSalvata);
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    document.querySelector('[data-modal="picker-nuova-impresa"]')?.remove();
  }

  // --- API pubblica per consumer esterni ---

  getSelected(): ImpresaLite | null {
    return this.selected;
  }

  setSelected(impresa: ImpresaLite | null): void {
    if (impresa) {
      this.selectImpresa(impresa);
    } else {
      this.clearSelection();
    }
  }

  // --- Logica interna ---

  private scheduleSearch(value: string): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (value.length < MIN_CHARS) {
      this.lastQuery = '';
      this.renderResults([]);
      return;
    }
    if (value === this.lastQuery) return;
    this.debounceTimer = setTimeout(() => this.executeSearch(value), DEBOUNCE_MS);
  }

  private async executeSearch(query: string): Promise<void> {
    this.lastQuery = query;
    const container = this.$('[data-picker-results]');
    if (!container) return;

    container.classList.remove('d-none');
    templateParseToElement('impresa-picker-loading', container, {});

    const result = await apiGet<{ risultati: ImpresaLite[]; count: number }>(
      `imprese/cerca?q=${encodeURIComponent(query)}`,
    );

    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }

    for (const r of result.data.risultati) this.cache.set(r.id, r);
    this.renderResults(result.data.risultati);
  }

  private renderResults(risultati: ImpresaLite[]): void {
    const container = this.$('[data-picker-results]');
    if (!container) return;
    if (risultati.length === 0 && this.lastQuery === '') {
      container.classList.add('d-none');
      container.innerHTML = '';
      return;
    }
    container.classList.remove('d-none');
    templateParseToElement('impresa-picker-results', container, {
      risultati,
      empty: risultati.length === 0,
    });
  }

  private selectImpresa(impresa: ImpresaLite): void {
    this.selected = impresa;
    const input = this.$('[data-picker-input]') as HTMLInputElement | null;
    const display = this.$('[data-picker-selected]');
    const container = this.$('[data-picker-results]');
    if (input) input.value = '';
    if (container) {
      container.classList.add('d-none');
      container.innerHTML = '';
    }
    if (display) {
      display.classList.remove('d-none');
      templateParseToElement('impresa-picker-selected', display, { impresa });
    }
    this.dispatch('impresa-selected', impresa);
  }

  private clearSelection(): void {
    this.selected = null;
    const display = this.$('[data-picker-selected]');
    if (display) {
      display.classList.add('d-none');
      display.innerHTML = '';
    }
    this.dispatch('impresa-cleared');
  }

  // --- "+ Nuova impresa" modal ---

  private openNuovaImpresaModal(): void {
    const modalEl = this.ensureModal();
    const editEl = modalEl.querySelector('wc-edit-impresa') as HTMLElement & { reset?: () => void } | null;
    editEl?.reset?.();
    this.getModalInstance(modalEl)?.show();
  }

  private ensureModal(): HTMLElement {
    const id = 'modalPickerNuovaImpresa';
    let modalEl = document.getElementById(id);
    if (modalEl) return modalEl;

    const tpl = this.$('[data-template="nuova-impresa-modal"]');
    if (tpl) {
      const cloned = tpl.firstElementChild?.cloneNode(true) as HTMLElement | undefined;
      if (cloned) {
        cloned.id = id;
        document.body.appendChild(cloned);
        modalEl = cloned;
        return modalEl;
      }
    }

    // fallback: costruisce shell minima via template Nunjucks
    const wrap = document.createElement('div');
    templateParseToElement('impresa-picker-modal-shell', wrap, { id });
    const built = wrap.firstElementChild as HTMLElement;
    document.body.appendChild(built);
    return built;
  }

  private handleImpresaSalvata = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { impresa?: ImpresaLite } | undefined;
    if (!detail?.impresa) return;
    const fromPicker = (e.target as HTMLElement)?.closest('#modalPickerNuovaImpresa');
    if (!fromPicker) return;
    this.cache.set(detail.impresa.id, detail.impresa);
    this.getModalInstance(document.getElementById('modalPickerNuovaImpresa'))?.hide();
    this.selectImpresa(detail.impresa);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getModalInstance(el: Element | null): { show(): void; hide(): void; dispose(): void } | null {
    if (!el) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }
}
